import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase';
import { dashboardCardClass } from './DashboardChrome';
import { DEVELLO_API_ORIGIN } from '../../utils/asyncReliability';

async function getToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token;
}

async function apiFetch(path, options = {}) {
  const token = await getToken();
  const resp = await fetch(`${DEVELLO_API_ORIGIN}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// ── Single member card ────────────────────────────────────────
function MemberCard({ member, onDeactivate, onReactivate, onDelete }) {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleToggleStatus = async () => {
    setBusy(true);
    try {
      if (member.status === 'active') await onDeactivate(member.id);
      else await onReactivate(member.id);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusy(true);
    try {
      await onDelete(member.id);
    } catch (e) {
      alert(e.message);
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-2xl px-5 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-cyan-400 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {(member.full_name || member.email || '?')[0].toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 text-sm truncate">{member.full_name || '—'}</p>
            <p className="text-xs text-gray-500 truncate">{member.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${member.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
            {member.status === 'active' ? 'Active' : 'Deactivated'}
          </span>
          <button
            onClick={handleToggleStatus}
            disabled={busy}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${member.status === 'active' ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'}`}
          >
            {busy ? '...' : member.status === 'active' ? 'Deactivate' : 'Reactivate'}
          </button>
          <button
            onClick={handleDelete}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 text-red-600 bg-red-50 hover:bg-red-100"
          >
            {busy ? '...' : confirmDelete ? 'Confirm?' : 'Delete'}
          </button>
        </div>
      </div>
      {confirmDelete && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-2.5">
          <p className="text-xs text-red-700">
            Permanently remove <strong>{member.full_name || member.email}</strong>? Their name will still show on past project entries.
          </p>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            className="text-xs text-slate-500 hover:text-slate-700 shrink-0 font-medium"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────
const TeamMembersSection = ({ userPlan }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [inviteToken, setInviteToken] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteError, setInviteError] = useState('');

  const isPro = userPlan === 'pro';
  const activeCount = members.filter((m) => m.status === 'active').length;

  const loadMembers = useCallback(async () => {
    try {
      const data = await apiFetch('/api/team/members');
      setMembers(data.members || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInviteStatus = useCallback(async () => {
    try {
      const data = await apiFetch('/api/team/invite-status');
      if (data.invite) setInviteToken(data.invite.token);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!isPro) { setLoading(false); return; }
    loadMembers();
    loadInviteStatus();
  }, [isPro, loadMembers, loadInviteStatus]);

  const handleCreateInvite = async (forceNew = false) => {
    setInviteLoading(true);
    setInviteError('');
    try {
      const data = await apiFetch('/api/team/invite', {
        method: 'POST',
        body: JSON.stringify({ forceNew }),
      });
      setInviteToken(data.token);
      setInviteCopied(false);
    } catch (e) {
      setInviteError(e.message);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/team-register?token=${inviteToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2500);
    });
  };

  const handleDeactivate = async (memberId) => {
    await apiFetch(`/api/team/members/${memberId}`, { method: 'DELETE' });
    setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, status: 'deactivated' } : m));
  };

  const handleReactivate = async (memberId) => {
    await apiFetch(`/api/team/members/${memberId}/reactivate`, { method: 'POST' });
    setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, status: 'active' } : m));
  };

  const handleDelete = async (memberId) => {
    await apiFetch(`/api/team/members/${memberId}/remove`, { method: 'DELETE' });
    setMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  if (!isPro) {
    return (
      <div className={dashboardCardClass}>
        <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <h3 className="text-base font-semibold text-slate-900">Team Members</h3>
        </div>
        <div className="p-6 text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-900 mb-1">Pro Feature</p>
          <p className="text-sm text-gray-500">Upgrade to Pro to invite up to 2 team members and manage their project access.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={dashboardCardClass}>
        <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <h3 className="text-base font-semibold text-slate-900">Team Members</h3>
        </div>
        <div className="p-6 flex justify-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className={dashboardCardClass}>
      <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Team Members</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {activeCount} of 2 active team members · Pro plan
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {[0, 1].map((i) => (
            <div key={i} className={`w-3 h-3 rounded-full border-2 ${i < activeCount ? 'bg-primary border-primary' : 'bg-transparent border-gray-300'}`} />
          ))}
        </div>
      </div>

      <div className="p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {/* Invite Section */}
        <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-slate-50 to-cyan-50/40 p-5 dark:border-primary/30 dark:from-primary/15 dark:via-slate-900 dark:to-slate-900">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 dark:bg-primary/25">
              <svg className="h-5 w-5 text-primary dark:text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="mb-1 text-sm font-semibold text-gray-900 dark:text-slate-100">Invite a Team Member</h4>
              <p className="mb-3 text-xs text-gray-600 dark:text-slate-400">
                Send an invite link to your employee. They'll create their own account and you control what they can see.
                {activeCount >= 2 && " You\u2019ve reached the 2-member limit."}
              </p>

              {!inviteToken ? (
                <div>
                  <button
                    onClick={() => handleCreateInvite(false)}
                    disabled={inviteLoading || activeCount >= 2}
                    className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
                  >
                    {inviteLoading ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Generating...</>
                    ) : (
                      <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>Generate Invite Link</>
                    )}
                  </button>
                  {inviteError && <p className="text-red-600 text-xs mt-2">{inviteError}</p>}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 truncate rounded-xl border border-primary/25 bg-white px-3 py-2 font-mono text-xs text-gray-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {`${window.location.origin}/team-register?token=${inviteToken}`}
                    </div>
                    <button
                      onClick={handleCopyLink}
                      className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${inviteCopied ? 'bg-emerald-100 text-emerald-700' : 'bg-primary text-white hover:bg-primary-dark'}`}
                    >
                      {inviteCopied ? (
                        <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Copied!</>
                      ) : (
                        <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Copy</>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-500">Link expires in 7 days · One-time use</p>
                  {activeCount < 2 && (
                    <button
                      onClick={() => handleCreateInvite(true)}
                      disabled={inviteLoading}
                      className="text-xs text-primary hover:text-primary-dark font-medium disabled:opacity-50"
                    >
                      {inviteLoading ? 'Generating...' : 'Generate new link'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Member List */}
        {members.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-sm text-gray-500">No team members yet. Generate an invite link above to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Your Team</h4>
            {members.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                onDeactivate={handleDeactivate}
                onReactivate={handleReactivate}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* Info */}
        <div className="space-y-1 rounded-xl border border-primary/20 bg-primary/5 p-4 text-xs text-primary-dark dark:border-primary/30 dark:bg-primary/10 dark:text-cyan-200">
          <p className="font-semibold">To manage project access & permissions:</p>
          <p>Open a project → click <strong>Team Access</strong> in the sidebar to control exactly what each team member can see and do in that project.</p>
        </div>
      </div>
    </div>
  );
};

export default TeamMembersSection;
